/**
 * Dimensões naturais de uma imagem em data URL (PNG e JPEG), lendo só o
 * cabeçalho. Serve para o documento NUNCA ampliar uma foto além do tamanho
 * real dela só para preencher a página: o `fit` recebe o menor entre a caixa
 * máxima e o tamanho natural. Formato desconhecido devolve null e a caixa
 * máxima é usada como está.
 */

import { isDrawableByPdfMake } from "../pdf-image.js";

export interface ImageSize {
  width: number;
  height: number;
}

/** Quanto do base64 decodificar: o SOF de um JPEG com miniatura EXIF pode estar longe do início. */
const MAX_DECODE_BYTES = 512 * 1024;

function decodeHead(dataUrl: string): Uint8Array | null {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return null;
  const base64 = dataUrl.slice(comma + 1);
  // Corta em múltiplo de 4 para o decodificador não reclamar do fim.
  const wanted = Math.ceil((MAX_DECODE_BYTES * 4) / 3);
  const slice = base64.slice(0, Math.min(base64.length, wanted - (wanted % 4)));
  try {
    if (typeof atob === "function") {
      const binary = atob(slice);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return bytes;
    }
    const nodeBuffer = (globalThis as any).Buffer;
    if (nodeBuffer) return new Uint8Array(nodeBuffer.from(slice, "base64"));
  } catch {
    return null;
  }
  return null;
}

export function readImageSize(dataUrl: string | null | undefined): ImageSize | null {
  if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) return null;
  const bytes = decodeHead(dataUrl);
  if (!bytes || bytes.length < 24) return null;

  // PNG: assinatura + IHDR (largura e altura em big-endian nos bytes 16..23).
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    const width = ((bytes[16]! << 24) | (bytes[17]! << 16) | (bytes[18]! << 8) | bytes[19]!) >>> 0;
    const height = ((bytes[20]! << 24) | (bytes[21]! << 16) | (bytes[22]! << 8) | bytes[23]!) >>> 0;
    return width > 0 && height > 0 ? { width, height } : null;
  }

  // JPEG: percorre os segmentos até um SOFn (C0..CF, exceto C4/C8/CC), que traz altura e largura.
  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    let i = 2;
    while (i + 9 < bytes.length) {
      if (bytes[i] !== 0xff) { i++; continue; }
      const marker = bytes[i + 1]!;
      if (marker === 0xff) { i++; continue; }
      if (marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) { i += 2; continue; }
      const length = (bytes[i + 2]! << 8) | bytes[i + 3]!;
      const isSof = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
      if (isSof) {
        const height = (bytes[i + 5]! << 8) | bytes[i + 6]!;
        const width = (bytes[i + 7]! << 8) | bytes[i + 8]!;
        return width > 0 && height > 0 ? { width, height } : null;
      }
      if (marker === 0xda) break; // início dos dados: sem SOF antes disso não há tamanho
      i += 2 + length;
    }
    return null;
  }

  return null;
}

/**
 * Caixa do `fit`: a máxima, reduzida ao tamanho natural quando a imagem é
 * menor — proporção preservada pelo próprio fit, sem ampliar para preencher.
 */
export function fitBox(dataUrl: string, maxWidth: number, maxHeight: number): [number, number] {
  const size = readImageSize(dataUrl);
  if (!size) return [maxWidth, maxHeight];
  return [Math.min(maxWidth, size.width), Math.min(maxHeight, size.height)];
}

/**
 * O que o pdfmake CONSEGUE desenhar: só PNG e JPEG. Qualquer outra coisa —
 * GIF, WEBP, um PDF anexado por engano, um marcador de imagem removida ou um
 * base64 truncado — faz o motor lançar "Unknown image format" e derruba a
 * geração do documento inteiro.
 *
 * Um documento técnico não pode deixar de ser emitido por causa de uma
 * imagem que o motor não entende: o builder verifica aqui antes de criar o
 * node `image:` e, quando não dá, desenha a caixa de "área reservada" no
 * lugar. É diferente da validação de upload (que decide o que o usuário pode
 * anexar) — esta é a última barreira, na hora de desenhar.
 */
export function isPdfDrawableImage(value: unknown): value is string {
  // A regra de "o que o pdfmake desenha" mora num lugar só. Repeti-la aqui
  // foi o que permitiu, noutros pontos, um gif ou webp passar por bom.
  return isDrawableByPdfMake(value);
}
